import {
  ActionIcon,
  CloseButton,
  Flex,
  Group,
  Menu,
  Text,
  Tooltip,
} from "@mantine/core";
import NavbarToggler from "../../../components/View/NavbarToggler";
import {
  IconCalendarWeek,
  IconChevronDown,
  IconSpeakerphone,
} from "@tabler/icons-react";
import { useReportingStore } from "../../../stores/ReportingStore";
import { DatePickerInput } from "@mantine/dates";
import { useLanguage } from "../../../stores/LanguageStore";
import {
  defaultDateRange,
  getLastWeekStartAndEnd,
  getLastXMonthsRange,
  getMonthStartAndEnd,
  getWeekStartAndEnd,
} from "../../../functions/dates";
import CampaignsSelector from "../../../components/Selectors/CampaignsSelector";
import AllAccountsSelector from "./AllAccountsSelector";
import { useCallback, useEffect } from "react";
import {
  getGlobalReport,
  getGlobalReportByDate,
  getGlobalReportByDateDetailed,
  getGlobalReportDetailed,
  handleDateStats,
} from "../../../api/ReportingAPI";
import { useWindowScroll } from "@mantine/hooks";
import ReportingExporter from "./export/ReportingExporter";

const ReportingHeader = () => {
  const lang = useLanguage((s) => s.language);
  const dateRange = useReportingStore((s) => s.dateRange);
  const setDateRange = useReportingStore((s) => s.setDateRange);
  const programmations = useReportingStore((s) => s.programmations);
  const setProgrammations = useReportingStore((s) => s.setProgrammations);
  const integrations = useReportingStore((s) => s.integrations);
  const setLoading = useReportingStore((s) => s.setLoading);
  const setLoadingDate = useReportingStore((s) => s.setLoadingDate);
  const setProgStats = useReportingStore((s) => s.setProgStats);
  const setLoadingProgStats = useReportingStore((s) => s.setLoadingProgStats);

  const [scroll] = useWindowScroll();

  const handleDateChange = (v) => {
    setDateRange({
      start_date: v[0],
      end_date: v[1],
    });
  };

  const handleLoad = useCallback(async () => {
    //#region Initialization
    if (dateRange.start_date && !dateRange.end_date) {
      return;
    }
    let finalDateRange =
      dateRange.start_date && dateRange.end_date ? dateRange : defaultDateRange;
    let main;
    let secondary = getGlobalReportByDate;

    if (finalDateRange.end_date) {
      let newEndDate = new Date(finalDateRange?.end_date);
      // newEndDate.setDate(newEndDate.getDate() + 1);

      finalDateRange.end_date = newEndDate;
    }

    async function getter(prog) {
      return getGlobalReportByDateDetailed({
        date_range: {
          start_date: new Date(finalDateRange.start_date).toLocaleDateString(
            "en-CA"
          ),
          end_date: new Date(finalDateRange.end_date).toLocaleDateString(
            "en-CA"
          ),
        },
        limit_to_programmation_ids: [prog.id],
        limit_to_integration_ids: integrations.map((x) => x.id),
        shouldUpdate: false,
      }).then((response) => {
        let newStats = handleDateStats(response.data.statistics, true);

        let actualProgStats = useReportingStore.getState().progStats;
        let newProgs = [...actualProgStats];
        newProgs.push({ prog, stats: newStats });

        setProgStats(newProgs.flat());
      });
    }
    //#endregion

    if (programmations.length > 0 || integrations.length > 0) {
      main = getGlobalReportDetailed;
      secondary = getGlobalReportByDateDetailed;
    }
    if (
      programmations.length === 0 &&
      integrations.length === 0 &&
      !dateRange.end_date
    ) {
      main = getGlobalReport;
    }

    setLoadingDate(true);
    secondary({
      date_range: {
        start_date: new Date(finalDateRange.start_date).toLocaleDateString(
          "en-CA"
        ),
        end_date: new Date(finalDateRange.end_date).toLocaleDateString("en-CA"),
      },
      limit_to_integration_ids: integrations.map((x) => x.id),
      limit_to_programmation_ids: programmations.map((x) => x.id),
    }).finally(() => setLoadingDate(false));

    if (main) {
      setLoading(true);
      main({
        date_range: {
          start_date: new Date(finalDateRange.start_date).toLocaleDateString(
            "en-CA"
          ),
          end_date: new Date(finalDateRange.end_date).toLocaleDateString(
            "en-CA"
          ),
        },
        limit_to_integration_ids: integrations.map((x) => x.id),
        limit_to_programmation_ids: programmations.map((x) => x.id),
      }).finally(() => setLoading(false));
    }

    if (programmations.length > 1) {
      setProgStats([]);
      setLoadingProgStats(true);
      Promise.allSettled(programmations.map((item) => getter(item))).finally(
        () => setLoadingProgStats(false)
      );
    }
  }, [
    dateRange,
    programmations,
    integrations,
    setLoadingDate,
    setProgStats,
    setLoading,
    setLoadingProgStats,
  ]);

  const handleProgrammationsChange = (v) => {
    if (v.length < 10) {
      setProgrammations(v);
      if (!dateRange?.start_date && v?.[0]) {
        let start = new Date(v[0].date_start);
        let end = new Date(v[0].date_start);
        end.setDate(end.getDate() + 50);

        setDateRange({
          start_date: start,
          end_date: end,
        });
      }
    }
  };

  useEffect(() => {
    handleLoad();
  }, [handleLoad]);

  return (
    <Group
      pos={"sticky"}
      top={0}
      py={9}
      px={32}
      pl={96}
      align="start"
      bg={scroll.y > 0 ? "#fff" : "var(--mantine-color-primary-2)"}
      style={{
        zIndex: 7,
        boxShadow: scroll.y > 0 ? "#00000012 6px 0 4px" : "none",
        borderBottom: "1px solid #00000012",
      }}
      className="reporting-header"
    >
      <NavbarToggler pos="absolute" left={12} top={6} style={{ zIndex: 4 }} />

      {/* <ThemeIcon
        size={"xl"}
        radius={"md"}
        variant="filled"
        color="primary"
      >
        <IconChartPie size={32} />
      </ThemeIcon> */}

      <Text fw={700} fz={24} mr={32}>
        {lang.reports.title}
      </Text>

      <DatePickerInput
        type="range"
        placeholder={lang.reports.period}
        leftSection={<IconCalendarWeek size={18} />}
        value={[dateRange.start_date, dateRange.end_date]}
        onChange={handleDateChange}
        maxDate={new Date()}
        w={dateRange.start_date ? 320 : 300}
        radius={"md"}
        styles={{
          input: {
            borderColor: "var(--mantine-color-primary-0)",
          },
        }}
        valueFormat="DD / MM / YYYY"
        size="md"
        rightSectionWidth={dateRange.start_date ? 75 : 50}
        rightSection={
          <Group gap={"xs"} wrap="nowrap">
            {dateRange.start_date && (
              <CloseButton
                onClick={() =>
                  setDateRange({ start_date: null, end_date: null })
                }
              />
            )}
            <Menu>
              <Menu.Target>
                <Tooltip
                  label={lang.reporting.period_chevron}
                  position="top"
                  disabled
                >
                  <ActionIcon variant="subtle">
                    <IconChevronDown />
                  </ActionIcon>
                </Tooltip>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Item
                  onClick={() => {
                    let thisWeek = getWeekStartAndEnd(new Date());
                    setDateRange({
                      start_date: thisWeek.weekStart,
                      end_date: thisWeek.weekEnd,
                    });
                  }}
                >
                  {lang.reporting.period_week}
                </Menu.Item>
                <Menu.Item
                  onClick={() => {
                    let result = getLastWeekStartAndEnd(new Date());
                    setDateRange({
                      start_date: result.weekStart,
                      end_date: result.weekEnd,
                    });
                  }}
                >
                  {lang.reporting.period_last_week}
                </Menu.Item>
                <Menu.Item
                  onClick={() => {
                    let thisMonth = getMonthStartAndEnd(new Date());
                    setDateRange({
                      start_date: thisMonth.monthStart,
                      end_date: thisMonth.monthEnd,
                    });
                  }}
                >
                  {lang.reporting.period_month}
                </Menu.Item>
                <Menu.Item
                  onClick={() => {
                    let result = getLastXMonthsRange(new Date(), 3);
                    setDateRange({
                      start_date: result.start,
                      end_date: result.end,
                    });
                  }}
                >
                  {lang.reporting.period_months.replace("#", "3")}
                </Menu.Item>
                <Menu.Item
                  onClick={() => {
                    let result = getLastXMonthsRange(new Date(), 6);
                    setDateRange({
                      start_date: result.start,
                      end_date: result.end,
                    });
                  }}
                >
                  {lang.reporting.period_months.replace("#", "6")}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        }
      />

      <Flex flex={1} miw={200}>
        <CampaignsSelector
          leftSection={<IconSpeakerphone size={18} />}
          onChange={handleProgrammationsChange}
          values={programmations}
          placeholder={lang.reports.campaigns}
          size={"md"}
        />
      </Flex>

      <Flex flex={1} miw={200}>
        <AllAccountsSelector />
      </Flex>

      <ReportingExporter />
    </Group>
  );
};

export default ReportingHeader;
